"use client";

import {
  BookMarked,
  BookOpen,
  Building2,
  Calendar,
  FileText,
  Hash,
  Layers,
  Loader2,
} from "lucide-react";
import Image from "next/image";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export interface BookData {
  key: string;
  title: string;
  author: string;
  authorKeys: string[];
  year: number | null;
  isbn: string | null;
  allIsbns: string[];
  coverUrl: string | null;
  coverUrlSmall: string | null;
  coverUrlLarge: string | null;
  publisher: string | null;
  allPublishers: string[];
  pages: number | null;
  subjects: string[];
  languages: string[];
  editionCount: number;
}

export interface PageRange {
  from: string;
  to: string;
}

export interface ChapterInfo {
  title: string;
  number: string;
}

interface BookSelectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  book: BookData | null;
  onCite: (
    book: BookData,
    pageRange?: PageRange,
    chapter?: ChapterInfo
  ) => void;
  loading?: boolean;
}

export function BookSelectModal({
  open,
  onOpenChange,
  book,
  onCite,
  loading = false,
}: BookSelectModalProps) {
  const [pageFrom, setPageFrom] = useState("");
  const [pageTo, setPageTo] = useState("");
  const [chapterTitle, setChapterTitle] = useState("");
  const [chapterNumber, setChapterNumber] = useState("");
  const [coverError, setCoverError] = useState(false);

  // Reset form when book changes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setPageFrom("");
      setPageTo("");
      setChapterTitle("");
      setChapterNumber("");
      setCoverError(false);
    }
    onOpenChange(newOpen);
  };

  const handleCite = () => {
    if (!book) {
      return;
    }

    const pageRange =
      pageFrom || pageTo ? { from: pageFrom, to: pageTo } : undefined;

    const chapter =
      chapterTitle || chapterNumber
        ? { title: chapterTitle, number: chapterNumber }
        : undefined;

    onCite(book, pageRange, chapter);
  };

  if (!book) {
    return null;
  }

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookMarked className="size-5 text-primary/70" />
            Book Details
          </DialogTitle>
          <DialogDescription>
            Review the information and optionally specify pages or chapter to
            cite
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-2">
          {/* Book Info Section */}
          <div className="flex flex-col gap-4 min-[400px]:flex-row min-[400px]:gap-6">
            {/* Cover Image */}
            <div className="shrink-0">
              {book.coverUrlLarge && !coverError ? (
                <div className="relative h-36 w-24 overflow-hidden rounded-lg border border-border/40 shadow-soft-md min-[400px]:h-48 min-[400px]:w-32">
                  <Image
                    alt={`Cover of ${book.title}`}
                    className="object-cover"
                    fill
                    onError={() => setCoverError(true)}
                    sizes="128px"
                    src={book.coverUrlLarge}
                  />
                  <div className="absolute inset-0 rounded-lg ring-1 ring-black/5 ring-inset" />
                </div>
              ) : (
                <div className="flex h-36 w-24 items-center justify-center rounded-lg border border-border/40 bg-muted/60 shadow-soft min-[400px]:h-48 min-[400px]:w-32">
                  <BookOpen className="size-12 text-muted-foreground/40" />
                </div>
              )}
            </div>

            {/* Metadata */}
            <div className="min-w-0 flex-1 space-y-3">
              <div>
                <h3 className="mb-1 text-balance font-semibold text-lg leading-tight">
                  {book.title}
                </h3>
                <p className="text-muted-foreground text-sm">
                  by {book.author}
                </p>
              </div>

              <div className="grid gap-2 text-sm">
                {book.year && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="size-4 shrink-0" />
                    <span>First published {book.year}</span>
                  </div>
                )}

                {book.publisher && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Building2 className="size-4 shrink-0" />
                    <span className="truncate">{book.publisher}</span>
                    {book.allPublishers.length > 1 && (
                      <span className="rounded bg-muted px-1.5 py-0.5 text-xs">
                        +{book.allPublishers.length - 1} more
                      </span>
                    )}
                  </div>
                )}

                {book.pages && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <FileText className="size-4 shrink-0" />
                    <span>{book.pages} pages</span>
                  </div>
                )}

                {book.isbn && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Hash className="size-4 shrink-0" />
                    <span className="font-mono text-xs">{book.isbn}</span>
                    {book.allIsbns.length > 1 && (
                      <span className="rounded bg-muted px-1.5 py-0.5 text-xs">
                        +{book.allIsbns.length - 1} editions
                      </span>
                    )}
                  </div>
                )}

                {book.editionCount > 1 && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Layers className="size-4 shrink-0" />
                    <span>{book.editionCount} editions available</span>
                  </div>
                )}
              </div>

              {/* Subjects/Topics */}
              {book.subjects.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {book.subjects.map((subject) => (
                    <span
                      className="rounded-full border border-border/40 bg-muted/70 px-2 py-1 text-muted-foreground text-xs"
                      key={subject}
                    >
                      {subject}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Citation Options */}
          <div className="space-y-4 border-border/60 border-t pt-2">
            <h4 className="font-medium text-muted-foreground text-sm">
              Citation Options (optional)
            </h4>

            {/* Page Range */}
            <div className="space-y-2">
              <Label className="text-sm">Page Range</Label>
              <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 sm:gap-3">
                <div className="relative flex-1">
                  <Input
                    className="pl-8"
                    inputMode="numeric"
                    onChange={(e) => setPageFrom(e.target.value)}
                    pattern="[0-9]*"
                    placeholder="From"
                    type="text"
                    value={pageFrom}
                  />
                  <span className="absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground text-xs">
                    p.
                  </span>
                </div>
                <span className="text-muted-foreground">—</span>
                <div className="relative flex-1">
                  <Input
                    className="pl-8"
                    inputMode="numeric"
                    onChange={(e) => setPageTo(e.target.value)}
                    pattern="[0-9]*"
                    placeholder="To"
                    type="text"
                    value={pageTo}
                  />
                  <span className="absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground text-xs">
                    p.
                  </span>
                </div>
              </div>
              <p className="text-muted-foreground text-xs">
                Leave empty to cite the entire book
              </p>
            </div>

            {/* Chapter/Section */}
            <div className="space-y-2">
              <Label className="text-sm">Chapter or Section</Label>
              <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                <Input
                  onChange={(e) => setChapterTitle(e.target.value)}
                  placeholder="Chapter title (e.g., 'Introduction')"
                  type="text"
                  value={chapterTitle}
                />
                <Input
                  className="w-full sm:w-20"
                  onChange={(e) => setChapterNumber(e.target.value)}
                  placeholder="Ch. #"
                  type="text"
                  value={chapterNumber}
                />
              </div>
              <p className="text-muted-foreground text-xs">
                Add chapter details if citing a specific section
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            disabled={loading}
            onClick={() => handleOpenChange(false)}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            className={cn(!book.isbn && "cursor-not-allowed opacity-50")}
            disabled={loading || !book.isbn}
            onClick={handleCite}
          >
            {loading ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <BookMarked className="size-4" />
                Cite this Book
              </>
            )}
          </Button>
        </DialogFooter>

        {!book.isbn && (
          <p className="text-center text-amber-600 text-xs dark:text-amber-400">
            This book doesn't have an ISBN. Try selecting a different edition.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
